/**
 * tsl-gerstner — the TSL (node) versions of the Gerstner evaluators, built
 * per wave set with the loop unrolled JS-side (count ≤ 24 constants). One
 * implementation feeds the water material AND the caustics projector; the
 * math mirrors GerstnerWaves.ts exactly (same clock, same constants).
 */

import { cos, float, normalize, sin, smoothstep, vec3 } from 'three/tsl';
import { FOOTPRINT_FADE_HI, FOOTPRINT_FADE_LO } from './waterLook.js';
import type { GerstnerWave } from './GerstnerWaves.js';

type FloatNode = ReturnType<typeof float>;
type UniformLike = { mul(v: unknown): FloatNode };

/**
 * Runtime sea-state modulation as NODES (uniforms): mirrors
 * GerstnerWaves.WaveModulation — amplitude scale + heading rotation
 * (cos/sin of the angle). Omitted = the authored constants, folded at build.
 */
export interface TslWaveModulation {
  ampScale: FloatNode;
  dirCos: FloatNode;
  dirSin: FloatNode;
}

export interface TslGerstner {
  displacement(px: FloatNode, pz: FloatNode): { dx: FloatNode; dy: FloatNode; dz: FloatNode };
  normal(px: FloatNode, pz: FloatNode): ReturnType<typeof normalize>;
  jacobian(px: FloatNode, pz: FloatNode): FloatNode;
  /**
   * R4 (2026-09-25): the normal with every wave the pixel FOOTPRINT (metres)
   * can no longer resolve faded out — a wave leaves between 4 and 2
   * footprints per wavelength — and the slope variance those waves carried,
   * Σ(1 − wᵢ)·(kᵢAᵢ)²/2, for the microfacet roughness (Bruneton 2010).
   * waterLook.filteredAlpha2 is its CPU oracle.
   */
  normalFiltered(px: FloatNode, pz: FloatNode, footprint: FloatNode): { normal: ReturnType<typeof normalize>; variance: FloatNode };
}

/** Footprint weight node for one wavelength (1 = resolved, 0 = folded into roughness). */
function footprintWeightNode(wavelength: number, footprint: FloatNode): FloatNode {
  return smoothstep(footprint.mul(FOOTPRINT_FADE_LO), footprint.mul(FOOTPRINT_FADE_HI), float(wavelength)) as FloatNode;
}

/**
 * One (phase, sin, cos) per wave per evaluation point, shared by every evaluator called at that point. WHY: the sea's
 * fragment stage calls normalFiltered, displacement AND jacobian at the same (wx, wz) — each rebuilt the phase and its
 * sin/cos for all 8 waves (and `normal` built cos twice), so the generated shader carried three copies of the wave sum's
 * trigonometry. Same operations on the same operands: the values are bit-identical, the shader shorter. Keyed by NODE
 * identity (the material passes the same wx / wz nodes); a different point gets its own entry.
 */
function makeTrigMemo<T>(phaseOf: (t: T, px: FloatNode, pz: FloatNode) => FloatNode, terms: readonly T[]) {
  const memo = new Map<unknown, Map<unknown, Array<{ s: FloatNode; c: FloatNode }>>>();
  return (px: FloatNode, pz: FloatNode): Array<{ s: FloatNode; c: FloatNode }> => {
    let byZ = memo.get(px);
    if (!byZ) { byZ = new Map(); memo.set(px, byZ); }
    let row = byZ.get(pz);
    if (!row) {
      row = terms.map((t) => { const phase = phaseOf(t, px, pz); return { s: sin(phase) as FloatNode, c: cos(phase) as FloatNode }; });
      byZ.set(pz, row);
    }
    return row;
  };
}

/** Build the three evaluators over a wave set and a shared time uniform. */
export function makeTslGerstner(waves: GerstnerWave[], uTime: UniformLike, mod?: TslWaveModulation): TslGerstner {
  if (mod) return makeModulatedTslGerstner(waves, uTime, mod);
  const phaseOf = (wv: GerstnerWave, px: FloatNode, pz: FloatNode): FloatNode =>
    float(wv.w).mul(px.mul(wv.dirX).add(pz.mul(wv.dirZ))).add(uTime.mul(wv.phi) as FloatNode);
  const trig = makeTrigMemo(phaseOf, waves);

  return {
    displacement(px, pz) {
      let dx: FloatNode = float(0);
      let dy: FloatNode = float(0);
      let dz: FloatNode = float(0);
      const tr = trig(px, pz);
      waves.forEach((wv, i) => {
        const c = tr[i].c;
        dy = dy.add(tr[i].s.mul(wv.amplitude));
        dx = dx.add(c.mul(wv.q * wv.amplitude * wv.dirX));
        dz = dz.add(c.mul(wv.q * wv.amplitude * wv.dirZ));
      });
      return { dx, dy, dz };
    },

    normal(px, pz) {
      let nx: FloatNode = float(0);
      let ny: FloatNode = float(1);
      let nz: FloatNode = float(0);
      const tr = trig(px, pz);
      waves.forEach((wv, i) => {
        const wa = wv.w * wv.amplitude;
        nx = nx.sub(tr[i].c.mul(wa * wv.dirX));
        nz = nz.sub(tr[i].c.mul(wa * wv.dirZ));
        ny = ny.sub(tr[i].s.mul(wv.q * wa));
      });
      return normalize(vec3(nx, ny, nz));
    },

    normalFiltered(px, pz, footprint) {
      let nx: FloatNode = float(0);
      let ny: FloatNode = float(1);
      let nz: FloatNode = float(0);
      let variance: FloatNode = float(0);
      const tr = trig(px, pz);
      waves.forEach((wv, i) => {
        const wgt = footprintWeightNode(wv.wavelength, footprint);
        const wa = wv.w * wv.amplitude;
        const cw = tr[i].c.mul(wgt);
        nx = nx.sub(cw.mul(wa * wv.dirX));
        nz = nz.sub(cw.mul(wa * wv.dirZ));
        ny = ny.sub(tr[i].s.mul(wgt).mul(wv.q * wa));
        variance = variance.add(float(1).sub(wgt).mul((wa * wa) / 2));
      });
      return { normal: normalize(vec3(nx, ny, nz)), variance };
    },

    jacobian(px, pz) {
      let jxx: FloatNode = float(1);
      let jzz: FloatNode = float(1);
      let jxz: FloatNode = float(0);
      const tr = trig(px, pz);
      waves.forEach((wv, i) => {
        const qaw = tr[i].s.mul(wv.q * wv.amplitude * wv.w);
        jxx = jxx.sub(qaw.mul(wv.dirX * wv.dirX));
        jzz = jzz.sub(qaw.mul(wv.dirZ * wv.dirZ));
        jxz = jxz.sub(qaw.mul(wv.dirX * wv.dirZ));
      });
      return jxx.mul(jzz).sub(jxz.mul(jxz));
    },
  };
}

/**
 * The modulated evaluators: every wave heading is rotated by (dirCos, dirSin)
 * and every amplitude scaled by ampScale — the same arithmetic as the CPU
 * samplers with a WaveModulation, so a wind-driven sea still answers the
 * height query the buoyancy and the rides ask.
 */
function makeModulatedTslGerstner(waves: GerstnerWave[], uTime: UniformLike, mod: TslWaveModulation): TslGerstner {
  const terms = waves.map((wv) => ({
    wv,
    dirX: mod.dirCos.mul(wv.dirX).sub(mod.dirSin.mul(wv.dirZ)) as FloatNode,
    dirZ: mod.dirSin.mul(wv.dirX).add(mod.dirCos.mul(wv.dirZ)) as FloatNode,
    amp: mod.ampScale.mul(wv.amplitude) as FloatNode,
  }));
  const phaseOf = (t: (typeof terms)[number], px: FloatNode, pz: FloatNode): FloatNode =>
    float(t.wv.w).mul(px.mul(t.dirX).add(pz.mul(t.dirZ))).add(uTime.mul(t.wv.phi) as FloatNode);
  const trig = makeTrigMemo(phaseOf, terms);

  return {
    displacement(px, pz) {
      let dx: FloatNode = float(0);
      let dy: FloatNode = float(0);
      let dz: FloatNode = float(0);
      const tr = trig(px, pz);
      for (const [i, t] of terms.entries()) {
        const c = tr[i].c;
        dy = dy.add(tr[i].s.mul(t.amp));
        const qa = c.mul(t.amp).mul(t.wv.q);
        dx = dx.add(qa.mul(t.dirX));
        dz = dz.add(qa.mul(t.dirZ));
      }
      return { dx, dy, dz };
    },

    normal(px, pz) {
      let nx: FloatNode = float(0);
      let ny: FloatNode = float(1);
      let nz: FloatNode = float(0);
      const tr = trig(px, pz);
      for (const [i, t] of terms.entries()) {
        const wa = t.amp.mul(t.wv.w);
        const cwa = tr[i].c.mul(wa);
        nx = nx.sub(cwa.mul(t.dirX));
        nz = nz.sub(cwa.mul(t.dirZ));
        ny = ny.sub(tr[i].s.mul(wa).mul(t.wv.q));
      }
      return normalize(vec3(nx, ny, nz));
    },

    normalFiltered(px, pz, footprint) {
      let nx: FloatNode = float(0);
      let ny: FloatNode = float(1);
      let nz: FloatNode = float(0);
      let variance: FloatNode = float(0);
      const tr = trig(px, pz);
      for (const [i, t] of terms.entries()) {
        const wgt = footprintWeightNode(t.wv.wavelength, footprint);
        const wa = t.amp.mul(t.wv.w);
        const cwa = tr[i].c.mul(wa).mul(wgt);
        nx = nx.sub(cwa.mul(t.dirX));
        nz = nz.sub(cwa.mul(t.dirZ));
        ny = ny.sub(tr[i].s.mul(wa).mul(wgt).mul(t.wv.q));
        variance = variance.add(float(1).sub(wgt).mul(wa.mul(wa).mul(0.5)));
      }
      return { normal: normalize(vec3(nx, ny, nz)), variance };
    },

    jacobian(px, pz) {
      let jxx: FloatNode = float(1);
      let jzz: FloatNode = float(1);
      let jxz: FloatNode = float(0);
      const tr = trig(px, pz);
      for (const [i, t] of terms.entries()) {
        const qaw = tr[i].s.mul(t.amp).mul(t.wv.q * t.wv.w);
        jxx = jxx.sub(qaw.mul(t.dirX).mul(t.dirX));
        jzz = jzz.sub(qaw.mul(t.dirZ).mul(t.dirZ));
        jxz = jxz.sub(qaw.mul(t.dirX).mul(t.dirZ));
      }
      return jxx.mul(jzz).sub(jxz.mul(jxz));
    },
  };
}
